import { 
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  updateProfile,
  signOut
} from 'firebase/auth';
import { doc, setDoc, getDoc, updateDoc } from 'firebase/firestore';
import { auth, db } from '../firebase/config';
import { format } from 'date-fns';

// User interface
export interface User {
  uid: string;
  fornavn: string;
  efternavn: string;
  email: string;
  brugernavn?: string;
  userIcon?: string;
  antal_af_spil: number;
  antal_vundne_praemier: number;
  antal_brugte_praemier: number;  // NEW - claimed/redeemed prizes
  antal_delte_praemier: number;   // NEW - prizes shared to friends
  createdAt: Date;
  Wiin_ID: string;
  Last_time_login: string;
  Status: string;
}

// Function to generate a unique Wiin_ID
const generateWiinId = (userId: string): string => {
  return `WIIN_${userId.substring(0, 8).toUpperCase()}`;
};

// Sign up user with email and password
export const signUpUser = async (
  email: string, 
  password: string, 
  fornavn: string, 
  efternavn: string
): Promise<User> => {
  try {
    // Create user in Firebase Authentication
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    
    // Add display name (first name + last name)
    await updateProfile(user, {
      displayName: `${fornavn} ${efternavn}`
    });
    
    // Create user document in Firestore
    const userData: User = {
      uid: user.uid,
      fornavn,
      efternavn,
      email,
      brugernavn: '',
      userIcon: '',
      antal_af_spil: 0,
      antal_vundne_praemier: 0,
      antal_brugte_praemier: 0,
      antal_delte_praemier: 0,
      createdAt: new Date(),
      Wiin_ID: generateWiinId(user.uid),
      Last_time_login: format(new Date(), 'dd:MM:yyyy'),
      Status: ''
    };
    
    // Save user data to Firestore
    await setDoc(doc(db, 'users', user.uid), userData);
    
    return userData;
  } catch (error: any) {
    console.error('Error during sign up:', error);
    throw error;
  }
};

// Sign in with email and password
export const signInUser = async (email: string, password: string) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    
    // Update Last_time_login
    const userRef = doc(db, 'users', userCredential.user.uid);
    await updateDoc(userRef, {
      Last_time_login: format(new Date(), 'dd:MM:yyyy')
    });
    
    return userCredential.user;
  } catch (error) {
    console.error('Error during sign in:', error);
    throw error;
  }
};

// Get user profile data
export const getUserProfile = async (uid: string): Promise<User | null> => {
  try {
    const userDoc = await getDoc(doc(db, 'users', uid));
    if (userDoc.exists()) {
      return userDoc.data() as User;
    }
    return null;
  } catch (error) {
    console.error('Error fetching user profile:', error);
    throw error;
  }
};

// Email validation - checks for @ and . in the string
export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

// Password validation - must contain uppercase letter, numbers, and be min 8 characters
export const isValidPassword = (password: string): boolean => {
  const passwordRegex = /^(?=.*[A-Z])(?=.*\d).{8,}$/;
  return passwordRegex.test(password);
};

// Name validation - should only contain letters
export const isValidName = (name: string): boolean => {
  const nameRegex = /^[A-Za-zÆØÅæøå\s-]+$/;
  return nameRegex.test(name);
};

// Update user brugernavn
export const updateUserBrugernavn = async (uid: string, brugernavn: string): Promise<void> => {
  try {
    const userRef = doc(db, 'users', uid);
    await updateDoc(userRef, {
      brugernavn: brugernavn
    });
  } catch (error) {
    console.error('Error updating brugernavn:', error);
    throw error;
  }
};

// Update user icon
export const updateUserIcon = async (uid: string, userIcon: string): Promise<void> => {
  try {
    const userRef = doc(db, 'users', uid);
    await updateDoc(userRef, {
      userIcon: userIcon
    });
  } catch (error) {
    console.error('Error updating userIcon:', error);
    throw error;
  }
};

// Helper function to get user icon path
export const getUserIconPath = (userIcon?: string): string => {
  if (userIcon && userIcon.trim() !== '') {
    return `/images/${userIcon}`;
  }
  return '/images/User_icon.png';
};

// Sign out user
export const signOutUser = async (): Promise<void> => {
  try {
    await signOut(auth);
  } catch (error) {
    console.error('Error during sign out:', error);
    throw error;
  }
}; 